import { Grid, Tab } from '@mui/material';
import { useState } from 'react';
import dayjs from 'dayjs';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  EstadosActivacionInstallOTEnumChoice,
  EstadosOrdenTrabajoEnumChoice,
  ROUTER_PATHS,
  a11yProps,
  gridSizeMdLg12,
  gridSizeMdLg6,
  useCheckPermissions,
  useTabsFormOnly,
} from '@/shared';
import {
  CustomTabPanel,
  FormTabsOnly,
  SingleBoxTableTabsOnly,
  SingleTableBoxScene,
} from '@/shared/components/ui';
import CustomRangeDate from '@/shared/components/ui/CustomRangeCalendar';
import ActivacionInstalacionPage from '@/erp/operaciones/activaciones/instalaciones/pages/tables/ActivacionInstalacionPage';

const TIPO_TRASLADO = 'TRASLADO';

/**
 * Módulo de Activaciones para TRASLADOS. Reutiliza la tabla-page de
 * instalaciones (`ActivacionInstalacionPage`) scopeada por
 * `tipoOrdenServicio='TRASLADO'` (mapea a agendamiento__tipo_orden_servicio) y
 * navegando a las rutas propias de traslado. Independiente de instalaciones.
 */
const ActivacionesTrasladoMainPage: React.FC = () => {
  useCheckPermissions(PermissionsEnum.api_view_ordentrabajo);
  const baseNavUrl = ROUTER_PATHS.activaciones.activacionesTrasladoNav;
  // Sin rango por defecto: los traslados pueden no tener fecha_instalacion aún y
  // el filtro de fecha (heredado de instalaciones) los excluiría. El operador
  // puede aplicar un rango manualmente si lo necesita.
  const [dateRange, setDateRange] = useState<[string, string]>(['', '']);
  const { handleMethodChange, valueTab } = useTabsFormOnly();

  return (
    <SingleTableBoxScene title={'Activaciones - Traslado'}>
      <Grid {...gridSizeMdLg12} container item justifyContent="flex-end" alignItems="center">
        <Grid {...gridSizeMdLg6} container item>
          <CustomRangeDate
            defaultValue={dateRange}
            label="Fecha instalacion"
            onChangeRangeDate={(val) => {
              setDateRange([
                dayjs(val[0]).format('YYYY-MM-DD'),
                dayjs(val[1]).format('YYYY-MM-DD'),
              ]);
            }}
            cleanFilter={() => setDateRange(['', ''])}
          />
        </Grid>
      </Grid>
      <SingleBoxTableTabsOnly>
        <FormTabsOnly onChange={handleMethodChange} value={valueTab}>
          <Tab label="Pendientes" value={1} {...a11yProps(1)} />
          <Tab label="Pendientes Recoordinadas" value={2} {...a11yProps(2)} />
          <Tab label="Gestionadas" value={3} {...a11yProps(3)} />
          <Tab label="Rechazadas" value={4} {...a11yProps(4)} />
        </FormTabsOnly>
      </SingleBoxTableTabsOnly>

      {/* ============ Pendientes ============ */}
      <CustomTabPanel value={valueTab} index={1}>
        <ActivacionInstalacionPage
          estadoOT={EstadosOrdenTrabajoEnumChoice.EN_ESPERA}
          estadoActivacion={EstadosActivacionInstallOTEnumChoice.EN_ESPERA}
          defaultDateRange={dateRange}
          tipoOrdenServicio={TIPO_TRASLADO}
          baseNavUrl={baseNavUrl}
          showFotosTraslado
        />
      </CustomTabPanel>

      {/* ============ Pendientes Recoordinadas ============ */}
      <CustomTabPanel value={valueTab} index={2}>
        <ActivacionInstalacionPage
          estadoOT={EstadosOrdenTrabajoEnumChoice.ESPERA_RECOORDINADA}
          estadoActivacion={EstadosActivacionInstallOTEnumChoice.EN_ESPERA}
          defaultDateRange={dateRange}
          tipoOrdenServicio={TIPO_TRASLADO}
          baseNavUrl={baseNavUrl}
          showFotosTraslado
        />
      </CustomTabPanel>

      {/* ============ Gestionadas ============ */}
      <CustomTabPanel value={valueTab} index={3}>
        <ActivacionInstalacionPage
          estadoActivacion={EstadosActivacionInstallOTEnumChoice.GESTIONADA}
          defaultDateRange={dateRange}
          tipoOrdenServicio={TIPO_TRASLADO}
          baseNavUrl={baseNavUrl}
          showFotosTraslado
        />
      </CustomTabPanel>

      {/* ============ Rechazadas ============ */}
      <CustomTabPanel value={valueTab} index={4}>
        <ActivacionInstalacionPage
          enableEdit={false}
          estadoActivacion={EstadosActivacionInstallOTEnumChoice.RECHAZADA}
          defaultDateRange={dateRange}
          tipoOrdenServicio={TIPO_TRASLADO}
          baseNavUrl={baseNavUrl}
          showFotosTraslado
        />
      </CustomTabPanel>
    </SingleTableBoxScene>
  );
};

export default ActivacionesTrasladoMainPage;
